<script>
/**
 * SelectProducts
 * @version 1.0.0
 * @since 2.3.0
 */
import { mapMutations, mapState } from 'vuex';
import { wizardListenerMixin } from '@/mixins/wizard';
import EzAutocomplete from '@/components/ui/Autocomplete/EzAutocomplete.vue';
import EzMaskInputSimple from '@/components/ui/MaskInputSimple/EzMaskInputSimple.vue';
import EzButton from '@/components/ui/Button';
import VPrice from '@/components/v3/elements/VPrice';
import { getCurrency, debounce } from '@/util/utils';
import VRecipeTotalInfo from '@/components/v3/patterns/VRecipeTotalInfo';

export default {
  mixins: [wizardListenerMixin],
  components: {
    EzAutocomplete,
    EzMaskInputSimple,
    EzButton,
    VPrice,
    VRecipeTotalInfo
  },
  data() {
    return {};
  },
  computed: {
    ...mapState('recipes', ['draft']),
    totalCogs() {
      if (!this.draft.items || this.draft.items.length === 0) {
        return 0;
      }

      return this.draft.items.reduce((total, item) => {
        const itemCogs = (item.price || 0) * (item.quantity || 0);
        return total + itemCogs;
      }, 0);
    },

    targetCogsPercent() {
      if (!this.draft.salesPrice || this.draft.salesPrice === 0) {
        return 0;
      }

      return parseFloat(((this.totalCogs / this.draft.salesPrice) * 100).toFixed(2));
    },
    currency() {
      return getCurrency() || {};
    },
    isIngredient() {
      return this.draft.type === 'ingredient';
    },
    gridCss() {
      if (this.isIngredient) {
        return {
          '--grid-columns': 'repeat(3, 1fr)',
          '--grid-columns-name-span': '1 / span 2',
          '--grid-columns-info-span': '3 / span 1',
        };
      }
      return {
        '--grid-columns': 'repeat(4, 1fr)',
        '--grid-columns-name-span': '1 / span 3',
        '--grid-columns-info-span': '4 / span 1',
      };
    },
  },
  methods: {
    ...mapMutations('recipes', ['UPDATE_DRAFT_RECIPE']),
    onItemSelect(item) {
      this.UPDATE_DRAFT_RECIPE({
        items: [...this.draft.items, item.selected],
      });
      this.$refs.autocomplete.clear();
      this.$refs.autocomplete.focus();
    },
    selectFistAvailableProduct(results) {
      if (results.length > 0) {
        this.onItemSelect(results[0]);
      }
    },
    isItemAdded(item) {
      return this.draft.items.some(i => i.id === item.id);
    },
    updateItem: debounce(function deb(type, recipeItem, value) {
      const updatedItems = this.draft.items.map(item => {
        if (item.id === recipeItem.id) {
          return { ...item, [type]: value };
        }
        return item;
      });
      this.UPDATE_DRAFT_RECIPE({ items: updatedItems });
    }, 300),
    async onNextStep() {
      this.$emit('stepCompleted');
    },
    onPreviousStep() {
      this.$emit('stepBack');
    },
    removeItem(item) {
      const updatedItems = this.draft.items.filter(i => i.id !== item.id);
      this.UPDATE_DRAFT_RECIPE({ items: updatedItems });
    },
    cogsPercentage(item) {
      const totalCogs = this.draft.items.reduce((acc, curr) => {
        if (curr.price && curr.quantity) {
          return acc + curr.price * curr.quantity;
        }
        return acc;
      }, 0);
      if (item.price && item.quantity) {
        const cogs = item.price * item.quantity;
        return ((cogs / totalCogs) * 100).toFixed(2);
      }
      return '0.00';
    },
  },
  watch: {
    'draft.items': {
      immediate: true,
      handler(newItems) {
        if(this.isIngredient){
          this.enableNextStep();
        } else if (newItems.length > 0 && newItems.every(item => item.quantity > 0)) {
          this.enableNextStep();
        } else {
          this.disableNextStep();
        }
      },
      deep: true,
    },
  },
};
</script>

<template>
  <div :style="gridCss" class="recipe-products">

    <div class="search-container" :class="{ 'has-items': draft?.items?.length }">
      <ez-autocomplete
        class="sticky-search"
        id="addProductInput"
        ref="autocomplete"
        placeholder="Search for an item"
        :source="`venue/recipes/search/recipe-items?type=${draft.type}`"
        searchProperty="name"
        label="Items"
        @selected="onItemSelect"
        @results="selectFistAvailableProduct"
        :has-connected-param="false"
      >
        <template #icon>
          <font-awesome-icon icon="search" />
        </template>
        <template #result="{ result }">
          <div class="item-result">
            <img :src="result.image" alt="" class="item-result__image" />
            <div class="item-result__info">
              <div class="item-result__title">
                <span :title="result.name">{{ result.name }}</span>
              </div>
              <div class="item-result__label mt-4">
                <span class="price-per-unit price-normal-weight">
                  <v-price
                    :price="result.price ?? 0"
                    :currency="result.currency || currency || {}"
                  />/ {{ result.unit.label }}
                </span>
                <span v-if="result.sku" class="mx-4">&#8226;</span>
                <span v-if="result.sku">{{ result.sku }}</span>
              </div>

            </div>
          </div>
        </template>
      </ez-autocomplete>
    </div>


    <div class="products-content">
      <div v-if="draft.items.length" class="item-grid mb-24">
        <div class="item-grid__wrapper" v-for="item in draft.items ?? []" :key="item.id">
          <div class="item-grid__name">
            <div class="product-info">
              <img v-if="item.image" :src="item.image" alt="" class="product-info__image" />
              <div class="product-info__details">
                <div class="product-info__name">{{ item.name }}</div>
                <div class="product-info__unit">{{ item.unit.name }}</div>
              </div>
            </div>
          </div>
          <div class="item-grid__info item-grid__cell--right">
            <span v-if="item.sku" class="light-gray">{{ item.sku }}</span>

            <div class="actions">
              <ez-button type="link" @click.stop="removeItem(item)" class="action">
                <font-awesome-icon icon="trash" />
              </ez-button>
            </div>
          </div>
          <div v-if="!isIngredient" class="item-grid__header">Quantity</div>
          <div v-else class="item-grid__header">Conversion</div>
          <div class="item-grid__header item-grid__cell--right">Price</div>
          <div class="item-grid__header item-grid__cell--right">COGS ($)</div>
          <div v-if="!isIngredient" class="item-grid__header item-grid__cell--right">COGS (%)</div>
          <div class="item-grid__cell">
            <ez-mask-input-simple
              :has-currency="false"
              name="quantity"
              :minValue="0"
              :value="item.quantity || 0"
              @input="updateItem('quantity', item, $event)"
              class="quantity__input"
              :precision="3"
            />
            <span class="unit-label" v-if="!isIngredient">{{ item.unit?.label }}</span>
            <span class="unit-label" v-else>{{ draft.unit?.label }}</span>
          </div>
          <div class="item-grid__cell item-grid__cell--right price-cell">
            <ez-mask-input-simple
              :currency="item.currency || currency || {}"
              :value="item.price || 0"
              @input="updateItem('price', item, $event)"
              class="price__input"
              :precision="4"
            />
            <div>
              <span class="unit-label">/ {{ item.unit?.label }}</span>
            </div>
          </div>
          <div class="item-grid__cell item-grid__cell--right">
            <v-price
              v-if="!isIngredient"
              :price="item.price * item.quantity || 0"
              :unit="(draft.unit || {})?.label"
              :currency="{ ...(item.currency || currency || {}), fractionDigits: 3 }"
            />
            <v-price
              v-else
              :price="item.price / item.quantity || 0"
              :unit="(draft.unit || {})?.label"
              :currency="{ ...(item.currency || currency || {}), fractionDigits: 3 }"
            />
          </div>
          <div v-if="!isIngredient" class="item-grid__cell item-grid__cell--right">
            <span>{{ cogsPercentage(item) }}%</span>
          </div>
        </div>
      </div>
      <hr v-if="draft.items.length" />
    </div>
    <div v-if="draft.type !== 'ingredient'" class="products-footer">
      <v-recipe-total-info
        v-if="draft.salesPrice !== null && draft.salesPrice !== undefined"
        :sales-price="draft.salesPrice"
        :target-cogs-amount="totalCogs"
        :target-cogs-percent="targetCogsPercent"
        :show-info-icon="true"
      />
      <v-recipe-total-info
        v-else
        :target-cogs-amount="totalCogs"
        :show-info-icon="false"
      />
    </div>

  </div>
</template>

<style lang="scss" scoped>
.products-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #eceef5;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 16px 0px;
  z-index: 50;
}

.product-info {
  display: flex;
  align-items: flex-start;
  gap: 8px;

  &__image {
    @include size(32px);
    border-radius: 3px;
    border: 1px solid #dee1e4;
    flex-shrink: 0;
  }

  &__details {
    flex: 1;
  }

  &__name {
    font-size: 14px;
    font-weight: 500;
    color: $color-gray-25;
    line-height: 1.4;
    margin-bottom: 2px;
  }

  &__unit {
    font-size: 12px;
    color: $color-gray-6C;
    line-height: 1.3;
  }
}
.recipe-products {
  width: 100%;
  max-width: 980px;
  margin: 0 auto;
  position: relative;

  @media (min-width: 1024px) {
    min-height: calc(100vh - 220px);
  }

  @media (max-width: 1023px) {
    min-height: calc(100vh - 280px);
  }
}

.search-container {
  position: sticky;
  top: 60px;
  z-index: 100;
  background: white;
  padding: 16px 0;
  margin-bottom: 16px;

  &.has-items {
    border-bottom: 1px solid #eceef5;
  }
}


.sticky-search {
  margin: 0 !important;
}

.item-result {
  @extend %flex-center;

  &__image {
    @include size(32px);
    border-radius: 3px;
    border: 1px solid #dee1e4;
  }

  &__info {
    margin-left: 8px;
  }

  &__titele {
    color: #252631;
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
  }

  &__label {
    color: #6c7995;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0;
    line-height: 14px;
    display: flex;
    align-items: center;

    .price-per-unit {
      font-weight: 600;
      display: inline-flex;
      align-items: center;
      white-space: nowrap;
    }

    .price-normal-weight {
      font-size: 12px !important;
      letter-spacing: 0;
      line-height: 14px;
      font-weight: 500 !important;

      :deep() .v-price__price {
        padding-right: 2px;
        font-size: 12px !important;
        letter-spacing: 0;
        line-height: 14px;
        font-weight: 500 !important;
      }
    }
  }
}

.item-grid {
  width: 100%;
  overflow: auto;

  .item-grid__info:hover {
    .actions {
      opacity: 1;
    }
  }
  .actions {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    color: $color-gray-6C;
    background: #fff;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
  }
  &__wrapper {
    display: grid;
    grid-template-rows: repeat(3, auto);
    grid-template-columns: var(--grid-columns);
  }

  &__name,
  &__info {
    padding: 10px 0;
    border-bottom: 1px dashed $color-gray-E1;
  }

  &__name {
    grid-column: var(--grid-columns-name-span);
    font-size: 14px;
    line-height: 1.4;
    color: $color-gray-25;
    &--new {
      display: flex;
      align-items: center;
      padding: 10px;
      background-color: $color-pastel-blue;
      border-bottom: 1px solid #a3bbff;

      :deep() .status--new {
        background-color: #4d7cfe;
        color: white;
      }
    }
  }

  &__info {
    position: relative;
    font-size: 12px;
    line-height: 1.5;
    &--new {
      padding: 10px;
      background-color: $color-pastel-blue;
      border-bottom: 1px solid #a3bbff;
    }
    grid-column: var(--grid-columns-info-span);
  }

  &__header {
    padding: 6px 0;
    font-size: 12px;
    line-height: 1.5;
    font-weight: 500;
    color: $color-gray-6C;
    border-bottom: 2px solid #eceef5;
  }

  &__cell {
    position: relative;
    display: flex;
    align-items: center;
    padding: 14px 0;
    border-bottom: 2px solid $color-primary-blue;

    &.price-cell {
      flex-direction: column;
      align-items: flex-end;
    }

    &--right {
      text-align: right;
      justify-content: flex-end;
    }

    .unit-label {
      margin-left: 4px;
      color: $color-gray-6C;
      font-size: 12px;
      line-height: 14px;
      font-weight: 500;
    }
    :deep() .ez-number-input,
    :deep() .mask-input-wrapper {
      display: inline-block; // Make it text alignable
      width: auto; // Reset inherited 100%
      max-width: 120px; // Limit
    }

    .quantity__input {
      :deep() .mask-input.mark-input--inline .mask-input__input {
        text-align: center;
      }
    }
    .price__input {
      :deep() .mask-input.mark-input--inline .mask-input__input {
        text-align: right;
      }
    }

    @media (min-width: 1440px) {
      :deep() .price__input.mask-input-wrapper .mask-input {
        width: 120px;
      }
    }
  }

  .light-gray {
    color: $color-gray-6C;
  }

  .dark-color {
    color: $color-gray-25;
  }
}
</style>
